import { Link, useRouter } from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon, KanbanSquareIcon, LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button, buttonVariants } from '@/components/ui/button';
import { useHideTask, type WorkspaceTaskRow } from '@/features/tasks';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * The three things most often done to a row, without opening it (spec 114
 * US3): open it on its board, copy a link to it, hide it.
 *
 * **Beside the row's link, never inside it** (FR-011). The row is one link —
 * the whole row is the target — and an interactive element inside a link is
 * invalid and announced badly; the page renders these as the link's sibling,
 * so Tab reaches them straight after their row and before the next one.
 *
 * Hiding is the one act here that changes the task (narrowing spec 068
 * FR-023). The row usually leaves the list the moment it is hidden, so the
 * confirmation is the only place left to take it back: it carries Undo.
 */
export function TaskRowActions({
  workspaceId,
  row,
}: {
  workspaceId: string;
  row: WorkspaceTaskRow;
}) {
  const router = useRouter();
  const { copy } = useCopyToClipboard();
  const { setTaskHidden, isPending: hiding } = useHideTask(workspaceId);
  const { taskId, title } = row;
  const canWork = useCanWork();

  const copyLink = async () => {
    // A clean link to the task — not to the sender's filters, which are theirs.
    const { href } = router.buildLocation({
      to: '/workspaces/$workspaceId/tasks',
      params: { workspaceId },
      search: { task: taskId, tab: 'details' },
    });
    if (await copy(`${window.location.origin}${href}`)) {
      toast('Link copied');
    } else {
      toast.error(
        'The link could not be copied — your browser refused the clipboard.',
      );
    }
  };

  // The board's card menu and this row hide a task the same way, with the
  // same Undo (spec 125 FR-011).
  const hide = (hidden: boolean) => {
    setTaskHidden({ id: taskId, title }, hidden);
  };

  return (
    <>
      {row.hasBoard ? (
        // A draft workflow has no board to open (spec 111 FR-013).
        <Link
          to="/workspaces/$workspaceId/workflows/$workflowKey/board"
          params={{ workspaceId, workflowKey: row.workflowKey }}
          search={{ task: taskId }}
          aria-label={`Open ${title} on board`}
          title="Open on board"
          className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
        >
          <KanbanSquareIcon aria-hidden="true" strokeWidth={1.5} />
        </Link>
      ) : null}
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Copy link to ${title}`}
        title="Copy link"
        onClick={() => {
          void copyLink();
        }}
      >
        <LinkIcon aria-hidden="true" strokeWidth={1.5} />
      </Button>
      {/* Hiding changes the board for everyone: members only (spec 189). */}
      {canWork ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`${row.hidden ? 'Unhide' : 'Hide'} ${title}`}
          title={row.hidden ? 'Unhide' : 'Hide'}
          disabled={hiding}
          onClick={() => {
            hide(!row.hidden);
          }}
        >
          {row.hidden ? (
            <EyeIcon aria-hidden="true" strokeWidth={1.5} />
          ) : (
            <EyeOffIcon aria-hidden="true" strokeWidth={1.5} />
          )}
        </Button>
      ) : null}
    </>
  );
}
